import {
  useCallback,
  useEffect,
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  wardrobeCompositionApiV1InternalWardrobeCompositionGet,
  type AdminClosetSizeBucket,
  type AdminWardrobeCompositionSummary,
  type AdminWardrobeCrosstab,
  type AdminWardrobeFacet,
  type AdminWardrobeFacetBucket,
  type AdminWardrobeFacetOverlap,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Tooltip, TooltipTrigger } from '@ui/components/base/tooltip/tooltip';
import { LoadingIndicator } from '@ui/components/application/loading-indicator/loading-indicator';
import { TextField } from '@ui/console/forms';
import { Callout, FailureText, Section, StatRow, StatTile } from '@ui/console/primitives';
import { Quantity, formatCount, formatPercent, plural } from '@ui/console/measures';
import { cx } from '@ui/utils/cx';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Timeline, TONE_VARIABLE, type TimeBucket } from './charts';
import { ActionDialog, Page } from './components';
import { GarmentImage } from './garment-image';
import {
  fetchGarmentDrilldown,
  type AdminWardrobeCompositionGarment,
  type GarmentDrilldownMatch,
} from './garment-drilldown-api';
import { errorDetail } from './operation-policy';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; data: AdminWardrobeCompositionSummary }
  | { status: 'failed'; detail: string };

const FACET_ORDER = [
  'colour',
  'category',
  'occasion',
  'season',
  'subcategory',
  'pattern',
  'material',
  'fit',
  'brand',
] as const;

const FACETS_PARAM = 'facets';
const SEARCH_PARAM = 'attribute';
const PAIRS_PARAM = 'pairs';
const FACET_LIMIT = 6;
const DEFAULT_CROSSTAB_LIMIT = 3;
const CHART_COLOURS = [
  TONE_VARIABLE.info,
  TONE_VARIABLE.success,
  TONE_VARIABLE.warning,
  TONE_VARIABLE.error,
  TONE_VARIABLE.neutral,
];

function count(value: number | undefined): number {
  return value ?? 0;
}

function facetPosition(key: string): number {
  const index = FACET_ORDER.indexOf(key as (typeof FACET_ORDER)[number]);
  return index < 0 ? FACET_ORDER.length : index;
}

function percent(numerator: number, denominator: number): string {
  return denominator > 0 ? formatPercent(numerator / denominator) : '—';
}

function compactCount(value: number): string {
  return Intl.NumberFormat(undefined, {
    notation: value >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  })
    .format(value)
    .replace('K', 'k');
}

function heatmapBackground(intensity: number, tone = TONE_VARIABLE.info): string {
  return `color-mix(in srgb, ${tone} ${Math.round(18 + intensity * 82)}%, var(--color-bg-primary))`;
}

function scaleIntensity(value: number, minimum: number, maximum: number): number {
  return maximum === minimum ? 1 : (value - minimum) / (maximum - minimum);
}

function median(values: readonly number[]): number | undefined {
  if (!values.length) return undefined;
  const ordered = [...values].sort((left, right) => left - right);
  const middle = Math.floor(ordered.length / 2);
  const upper = ordered[middle];
  if (upper === undefined) return undefined;
  return ordered.length % 2 ? upper : ((ordered[middle - 1] ?? upper) + upper) / 2;
}

function comparison(value: number, middle: number | undefined): string {
  if (middle === undefined || middle === 0 || value === 0) return 'median comparison unavailable';
  if (value === middle) return 'the same as the median bucket';
  const ratio = value > middle ? value / middle : middle / value;
  return `${ratio.toLocaleString(undefined, { maximumFractionDigits: 1 })}× ${
    value > middle ? 'larger' : 'smaller'
  } than the median bucket`;
}

// The backend gap-fills months with zero, so each month is one bar.
function monthTimeline(
  data: NonNullable<AdminWardrobeCompositionSummary['added_by_month']>,
): TimeBucket[] {
  return data.map((item) => {
    const start = Date.parse(`${item.month}-01T00:00:00Z`);
    return {
      start,
      label: new Date(start).toLocaleDateString(undefined, {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      }),
      counts: { garments: item.garments },
    };
  });
}

function bucketRank(
  buckets: readonly AdminWardrobeFacetBucket[],
  bucket: AdminWardrobeFacetBucket,
) {
  return (
    [...buckets]
      .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value))
      .findIndex((candidate) => candidate.value === bucket.value) + 1
  );
}

type FacetRendererProps = {
  facet: AdminWardrobeFacet;
  overlap?: AdminWardrobeFacetOverlap;
  query: string;
  pinned: string | undefined;
  onPin: (value: string | undefined) => void;
  onDrillDown: (title: string, matches: GarmentDrilldownMatch[]) => void;
};

function matchesValue(value: string, query: string, pinned: string | undefined): boolean {
  if (!query && !pinned) return true;
  return Boolean(
    (query && value.toLocaleLowerCase().includes(query)) || (pinned && value === pinned),
  );
}

function isDrillableFacetValue(value: string): boolean {
  return !['other', 'unknown'].includes(value.toLocaleLowerCase());
}

function OverlapHeatmapFacet({
  facet,
  overlap,
  query,
  pinned,
  onPin,
  onDrillDown,
}: FacetRendererProps) {
  if (!overlap) return <HorizontalFacet {...{ facet, query, pinned, onPin, onDrillDown }} />;
  const pairs = overlap.pairs ?? [];
  const values = [...(facet.buckets ?? [])]
    .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value))
    .slice(0, 12);
  if (!pairs.length || !values.length) {
    return <HorizontalFacet {...{ facet, query, pinned, onPin, onDrillDown }} />;
  }
  const pairCounts = new Map<string, number>(
    pairs.map((pair) => [[pair.a, pair.b].sort().join('\0'), pair.garments] as const),
  );
  const displayedValues = new Set(values.map((bucket) => bucket.value));
  const offDiagonalCounts = pairs
    .filter(
      (pair) => pair.a !== pair.b && displayedValues.has(pair.a) && displayedValues.has(pair.b),
    )
    .map((pair) => pair.garments);
  const offDiagonalMinimum = offDiagonalCounts.length ? Math.min(...offDiagonalCounts) : 0;
  const offDiagonalMaximum = offDiagonalCounts.length ? Math.max(...offDiagonalCounts) : 0;
  const diagonalCounts = values.map((bucket) => bucket.count);
  const diagonalMinimum = Math.min(...diagonalCounts);
  const diagonalMaximum = Math.max(...diagonalCounts);
  const cellSize = values.length <= 4 ? '3rem' : '2.25rem';

  return (
    <div
      className="flex flex-col gap-4"
      role="group"
      aria-label={`${facet.label} co-occurrence heatmap`}
    >
      <div className="overflow-x-auto pb-1">
        <div
          className="grid min-w-max gap-1"
          style={{ gridTemplateColumns: `7rem repeat(${values.length}, ${cellSize})` }}
        >
          <span />
          {values.map((bucket) => (
            <span
              key={bucket.value}
              className={cx(
                'truncate text-center text-[0.625rem] capitalize text-tertiary [writing-mode:vertical-rl]',
                !matchesValue(bucket.value, query, pinned) && 'opacity-25',
              )}
            >
              {bucket.value}
            </span>
          ))}
          {values.map((row) => (
            <div key={row.value} className="contents">
              <BucketMark
                facet={facet}
                bucket={row}
                query={query}
                pinned={pinned}
                onPin={onPin}
                onDrillDown={onDrillDown}
                className={cx(
                  'truncate rounded px-1 text-left text-xs capitalize text-secondary outline-brand focus-visible:outline-2',
                  !matchesValue(row.value, query, pinned) && 'opacity-25',
                )}
              >
                {row.value} ({compactCount(row.count)})
              </BucketMark>
              {values.map((column) => {
                const diagonal = row.value === column.value;
                const pair = [row.value, column.value].sort();
                const garments = diagonal ? row.count : pairCounts.get(pair.join('\0'));
                const label = diagonal
                  ? `${row.value}: ${formatCount(row.count)} garments with this value`
                  : garments === undefined
                    ? `${pair.join(' + ')}: —`
                    : `${pair.join(' + ')}: ${formatCount(garments)} garments together`;
                const intensity =
                  garments === undefined
                    ? 0
                    : diagonal
                      ? scaleIntensity(garments, diagonalMinimum, diagonalMaximum)
                      : scaleIntensity(garments, offDiagonalMinimum, offDiagonalMaximum);
                const active =
                  matchesValue(row.value, query, pinned) ||
                  matchesValue(column.value, query, pinned);
                const drillable =
                  isDrillableFacetValue(row.value) && isDrillableFacetValue(column.value);
                return (
                  <Tooltip
                    key={column.value}
                    title={diagonal ? row.value : pair.join(' + ')}
                    description={label}
                    delay={0}
                  >
                    <TooltipTrigger
                      aria-label={label}
                      className={cx(
                        'flex items-center justify-center rounded-sm border border-primary text-[0.6875rem] font-medium tabular-nums outline-brand transition-opacity focus-visible:outline-2',
                        !diagonal && garments !== undefined && intensity >= 0.55
                          ? 'text-white'
                          : 'text-primary',
                        !active && 'opacity-20',
                      )}
                      data-scale={diagonal ? 'diagonal' : 'off-diagonal'}
                      data-diagonal={diagonal ? 'true' : undefined}
                      data-intensity={garments === undefined ? undefined : intensity.toFixed(3)}
                      data-max-intensity={
                        !diagonal && garments === offDiagonalMaximum ? 'true' : undefined
                      }
                      onPress={
                        drillable
                          ? (event) => {
                              if (event.shiftKey) {
                                onPin(pinned === row.value ? undefined : row.value);
                                return;
                              }
                              onDrillDown(
                                diagonal ? row.value : `${row.value} × ${column.value}`,
                                diagonal
                                  ? [
                                      {
                                        key: facet.key as GarmentDrilldownMatch['key'],
                                        value: row.value,
                                      },
                                    ]
                                  : [
                                      {
                                        key: facet.key as GarmentDrilldownMatch['key'],
                                        value: row.value,
                                      },
                                      {
                                        key: facet.key as GarmentDrilldownMatch['key'],
                                        value: column.value,
                                      },
                                    ],
                              );
                            }
                          : undefined
                      }
                      style={{
                        width: cellSize,
                        height: cellSize,
                        color:
                          diagonal && intensity >= 0.55 ? 'var(--color-neutral-900)' : undefined,
                        background: diagonal
                          ? heatmapBackground(intensity, TONE_VARIABLE.warning)
                          : garments === undefined
                            ? 'var(--color-bg-secondary)'
                            : heatmapBackground(intensity),
                      }}
                    >
                      {garments === undefined ? '—' : compactCount(garments)}
                    </TooltipTrigger>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <ul
        className="grid gap-3 text-[0.625rem] text-tertiary sm:grid-cols-2"
        aria-label={`${facet.label} heatmap colour scales`}
      >
        {[
          {
            label: 'Garments with both values',
            minimum: offDiagonalMinimum,
            maximum: offDiagonalMaximum,
            tone: TONE_VARIABLE.info,
          },
          {
            label: 'Garments with this value',
            minimum: diagonalMinimum,
            maximum: diagonalMaximum,
            tone: TONE_VARIABLE.warning,
          },
        ].map((scale) => (
          <li key={scale.label} className="flex flex-col gap-1">
            <span>{scale.label}</span>
            <span
              role="img"
              aria-label={`${scale.label}: ${formatCount(scale.minimum)} to ${formatCount(scale.maximum)}`}
              className="h-2 rounded-full"
              style={{
                background: `linear-gradient(to right, ${heatmapBackground(0, scale.tone)}, ${heatmapBackground(1, scale.tone)})`,
              }}
            />
            <span className="flex justify-between tabular-nums" aria-hidden="true">
              <span>{compactCount(scale.minimum)}</span>
              <span>{compactCount(scale.maximum)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function facetCounted(facet: AdminWardrobeFacet): number {
  return facet.garments ?? facet.known + facet.unknown;
}

function bucketDetail(facet: AdminWardrobeFacet, bucket: AdminWardrobeFacetBucket): string {
  const counted = facetCounted(facet);
  const countedShare = counted > 0 ? formatPercent(bucket.share) : '—';
  const knownShare = bucket.value === 'unknown' ? '—' : percent(bucket.count, facet.known);
  return `Count ${formatCount(bucket.count)} · ${countedShare} of counted garments · ${knownShare} of known values · rank ${bucketRank(
    facet.buckets ?? [],
    bucket,
  )} of ${formatCount((facet.buckets ?? []).length)}`;
}

function BucketMark({
  facet,
  bucket,
  pinned,
  onPin,
  className,
  style,
  children,
  onDrillDown,
}: FacetRendererProps & {
  bucket: AdminWardrobeFacetBucket;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const detail = bucketDetail(facet, bucket);
  const active = pinned === bucket.value;
  const drillable = isDrillableFacetValue(bucket.value);
  return (
    <Tooltip title={bucket.value} description={detail} delay={0}>
      <TooltipTrigger
        aria-label={`${facet.label}, ${bucket.value}: ${detail}.${
          drillable
            ? ` Open matching garments; Shift+click to ${active ? 'remove' : 'set'} chart highlight.`
            : ''
        }`}
        aria-pressed={drillable ? active : undefined}
        onPress={
          drillable
            ? (event) => {
                // Shift preserves the old lightweight chart highlight without competing with drill-down.
                if (event.shiftKey) onPin(active ? undefined : bucket.value);
                else
                  onDrillDown(bucket.value, [
                    { key: facet.key as GarmentDrilldownMatch['key'], value: bucket.value },
                  ]);
              }
            : undefined
        }
        className={cx(className, active && 'ring-2 ring-brand')}
        style={style}
      >
        {children}
      </TooltipTrigger>
    </Tooltip>
  );
}

function HorizontalFacet({ facet, query, pinned, onPin, onDrillDown }: FacetRendererProps) {
  const [expanded, setExpanded] = useState(false);
  const allBuckets = facet.buckets ?? [];
  const buckets = query
    ? allBuckets.filter((bucket) => bucket.value.toLocaleLowerCase().includes(query))
    : allBuckets;
  const visible = expanded ? buckets : buckets.slice(0, FACET_LIMIT);
  const counted = facetCounted(facet);

  if (!visible.length) {
    return (
      <p className="text-sm text-tertiary">
        {query ? 'No values match this filter.' : 'No values were recorded for this attribute.'}
      </p>
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-1.5">
        {visible.map((bucket) => (
          <li key={bucket.value}>
            <BucketMark
              facet={facet}
              bucket={bucket}
              query={query}
              pinned={pinned}
              onPin={onPin}
              onDrillDown={onDrillDown}
              className="grid w-full min-w-0 grid-cols-[minmax(4.5rem,7rem)_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-1.5 py-1 text-left outline-brand hover:bg-secondary focus-visible:outline-2"
            >
              <span className="truncate text-xs text-secondary capitalize">{bucket.value}</span>
              <span className="block h-2 overflow-hidden rounded-full bg-secondary">
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${Math.min(100, Math.max(0, bucket.share * 100))}%`,
                    background: TONE_VARIABLE.info,
                  }}
                />
              </span>
              <span className="flex justify-end gap-1.5 text-xs tabular-nums">
                <Quantity value={formatCount(bucket.count)} raw={bucket.count} />
                <span className="w-10 text-right text-tertiary">
                  {counted > 0 ? formatPercent(bucket.share) : '—'}
                </span>
              </span>
            </BucketMark>
          </li>
        ))}
      </ul>
      {buckets.length > FACET_LIMIT ? (
        <button
          type="button"
          className="self-start rounded-md text-xs font-medium text-brand-secondary underline outline-brand focus-visible:outline-2"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? `Show top ${FACET_LIMIT}` : `Show all ${formatCount(buckets.length)}`}
        </button>
      ) : null}
    </>
  );
}

function CompositionFacet({ facet, query, pinned, onPin, onDrillDown }: FacetRendererProps) {
  const buckets = [...(facet.buckets ?? [])].sort(
    (left, right) => right.count - left.count || left.value.localeCompare(right.value),
  );
  if (!buckets.length) return <HorizontalFacet {...{ facet, query, pinned, onPin, onDrillDown }} />;
  const matched = (bucket: AdminWardrobeFacetBucket) =>
    !query || bucket.value.toLocaleLowerCase().includes(query);

  return (
    <div className="flex flex-col gap-3" role="group" aria-label={`${facet.label} composition`}>
      <div className="flex h-10 overflow-hidden rounded-lg bg-secondary">
        {buckets.map((bucket, index) =>
          bucket.count > 0 && bucket.share > 0 ? (
            <BucketMark
              key={bucket.value}
              facet={facet}
              bucket={bucket}
              query={query}
              pinned={pinned}
              onPin={onPin}
              onDrillDown={onDrillDown}
              className={cx(
                'h-full border-r border-primary outline-brand transition-opacity last:border-r-0 focus-visible:z-10 focus-visible:outline-2',
                !matched(bucket) && 'opacity-20',
              )}
              style={{
                width: `${Math.max(0, bucket.share * 100)}%`,
                background: CHART_COLOURS[index % CHART_COLOURS.length],
              }}
            >
              <span className="sr-only">{bucket.value}</span>
            </BucketMark>
          ) : null,
        )}
      </div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3">
        {buckets.map((bucket, index) => (
          <li
            key={bucket.value}
            className={cx('flex min-w-0 items-center gap-1.5', !matched(bucket) && 'opacity-40')}
          >
            <span
              className="size-2 shrink-0 rounded-sm"
              style={{ background: CHART_COLOURS[index % CHART_COLOURS.length] }}
              aria-hidden="true"
            />
            <span className="truncate capitalize text-secondary">{bucket.value}</span>
            <span className="ml-auto tabular-nums text-tertiary">
              {facetCounted(facet) > 0 ? formatPercent(bucket.share) : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ParetoFacet({ facet, query, pinned, onPin, onDrillDown }: FacetRendererProps) {
  const filtered = [...(facet.buckets ?? [])]
    .filter((bucket) => !query || bucket.value.toLocaleLowerCase().includes(query))
    .sort((left, right) => right.count - left.count || left.value.localeCompare(right.value));
  if (!filtered.length)
    return <HorizontalFacet {...{ facet, query, pinned, onPin, onDrillDown }} />;
  const top = filtered.slice(0, 10);
  const rest = filtered.slice(10);
  const total = filtered.reduce((sum, bucket) => sum + bucket.count, 0);
  const restCount = rest.reduce((sum, bucket) => sum + bucket.count, 0);
  const peak = Math.max(1, ...top.map((bucket) => bucket.count), restCount);
  const columns = top.length + (rest.length ? 1 : 0);

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={`${facet.label} bar chart`}>
      <div className="relative h-36 border-b border-secondary">
        <div
          className="absolute inset-0 grid items-stretch gap-1"
          style={{
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            gridTemplateRows: '100%',
          }}
        >
          {top.map((bucket) => (
            <BucketMark
              key={bucket.value}
              facet={facet}
              bucket={bucket}
              query={query}
              pinned={pinned}
              onPin={onPin}
              onDrillDown={onDrillDown}
              className="flex h-full items-end rounded-t-sm outline-brand focus-visible:outline-2"
            >
              <span
                className="block w-full rounded-t-sm"
                style={{
                  height: `${(bucket.count / peak) * 100}%`,
                  minHeight: bucket.count > 0 ? '2px' : undefined,
                  background: TONE_VARIABLE.info,
                }}
              />
            </BucketMark>
          ))}
          {rest.length ? (
            <Tooltip
              title={`${formatCount(rest.length)} others`}
              description={`${formatCount(restCount)} garments · ${total > 0 ? formatPercent(restCount / total) : '—'} of displayed values`}
              delay={0}
            >
              <TooltipTrigger
                aria-label={`${facet.label}, ${formatCount(rest.length)} others: ${formatCount(restCount)} garments.`}
                className="flex h-full items-end rounded-t-sm outline-brand focus-visible:outline-2"
              >
                <span
                  className="block w-full rounded-t-sm"
                  style={{
                    height: `${(restCount / peak) * 100}%`,
                    minHeight: restCount > 0 ? '2px' : undefined,
                    background: TONE_VARIABLE.neutral,
                  }}
                />
              </TooltipTrigger>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div
        className="grid gap-1 text-center text-[0.625rem] text-tertiary"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {top.map((bucket) => (
          <span key={bucket.value} className="truncate capitalize">
            {bucket.value}
          </span>
        ))}
        {rest.length ? <span>{formatCount(rest.length)} others</span> : null}
      </div>
    </div>
  );
}

function PieFacet({ facet, query, pinned, onPin, onDrillDown }: FacetRendererProps) {
  const buckets = [...(facet.buckets ?? [])].sort(
    (left, right) => right.count - left.count || left.value.localeCompare(right.value),
  );
  if (!buckets.length) return <HorizontalFacet {...{ facet, query, pinned, onPin, onDrillDown }} />;
  const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  let offset = 0;
  const stops = buckets.map((bucket, index) => {
    const start = offset;
    offset += total > 0 ? (bucket.count / total) * 100 : 0;
    const matches = !query || bucket.value.toLocaleLowerCase().includes(query);
    const colour = matches
      ? CHART_COLOURS[index % CHART_COLOURS.length]
      : 'var(--color-bg-secondary)';
    return `${colour} ${start}% ${offset}%`;
  });

  return (
    <div
      className="grid grid-cols-[7rem_minmax(0,1fr)] items-center gap-4"
      role="group"
      aria-label={`${facet.label} pie chart`}
    >
      <div
        role="img"
        tabIndex={0}
        aria-label={`${facet.label} pie: ${formatCount(total)} garments`}
        className="size-28 rounded-full outline-brand focus-visible:outline-2"
        style={{ background: `conic-gradient(${stops.join(', ')})` }}
      />
      <ul className="flex min-w-0 flex-col gap-1">
        <li className="text-xs font-medium text-secondary">{formatCount(total)} garments total</li>
        {buckets.map((bucket, index) => {
          const matches = !query || bucket.value.toLocaleLowerCase().includes(query);
          return (
            <li key={bucket.value} className={cx(!matches && 'opacity-30')}>
              <BucketMark
                facet={facet}
                bucket={bucket}
                query={query}
                pinned={pinned}
                onPin={onPin}
                onDrillDown={onDrillDown}
                className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1.5 rounded px-1 py-0.5 text-left outline-brand hover:bg-secondary focus-visible:outline-2"
              >
                <span
                  className="size-2 rounded-full"
                  style={{ background: CHART_COLOURS[index % CHART_COLOURS.length] }}
                  aria-hidden="true"
                />
                <span className="truncate text-xs capitalize text-secondary">{bucket.value}</span>
                <span className="text-xs tabular-nums text-tertiary">
                  {total > 0 ? formatPercent(bucket.count / total) : '—'}
                </span>
              </BucketMark>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const FACET_RENDERERS: Record<string, ComponentType<FacetRendererProps>> = {
  category: CompositionFacet,
  subcategory: ParetoFacet,
  brand: ParetoFacet,
  fit: PieFacet,
  pattern: PieFacet,
  material: HorizontalFacet,
  colour: OverlapHeatmapFacet,
  season: OverlapHeatmapFacet,
  occasion: OverlapHeatmapFacet,
};

function Facet({
  facet,
  overlap,
  query,
  pinned,
  onPin,
  onDrillDown,
}: {
  facet: AdminWardrobeFacet;
  overlap?: AdminWardrobeFacetOverlap;
  query: string;
  pinned: string | undefined;
  onPin: (value: string | undefined) => void;
  onDrillDown: (title: string, matches: GarmentDrilldownMatch[]) => void;
}) {
  const allBuckets = facet.buckets ?? [];
  const middle = median(allBuckets.map((bucket) => bucket.count));
  const selected = allBuckets.find((bucket) => bucket.value === pinned);
  const counted = facetCounted(facet);
  const Renderer = FACET_RENDERERS[facet.key] ?? HorizontalFacet;
  const multiValued = facet.key === 'colour' || facet.key === 'season' || facet.key === 'occasion';
  const longTail = facet.key === 'subcategory' || facet.key === 'brand';
  const orderedBuckets = [...allBuckets].sort(
    (left, right) => right.count - left.count || left.value.localeCompare(right.value),
  );
  const bucketTotal = orderedBuckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const topTenTotal = orderedBuckets.slice(0, 10).reduce((sum, bucket) => sum + bucket.count, 0);
  const valuesPerGarment = overlap?.values_per_garment ?? [];
  const valuesPerGarmentTotal = valuesPerGarment.reduce((sum, bucket) => sum + bucket.garments, 0);
  const singleValueGarments = valuesPerGarment.find((bucket) => bucket.values === '1')?.garments;
  const multipleValueShare =
    singleValueGarments === undefined || valuesPerGarmentTotal === 0
      ? undefined
      : 1 - singleValueGarments / valuesPerGarmentTotal;
  return (
    <section
      aria-labelledby={`facet-${facet.key}`}
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
    >
      <header>
        <h3 id={`facet-${facet.key}`} className="text-sm font-semibold text-primary">
          {facet.label}
        </h3>
        <p className="text-xs text-tertiary">
          {formatCount(facet.known)} known · {formatCount(facet.unknown)} unknown
          {multipleValueShare !== undefined ? (
            <span className="text-secondary">
              {' · '}
              {formatPercent(multipleValueShare)} of garments have more than one{' '}
              {facet.label.toLocaleLowerCase()}
            </span>
          ) : null}
          {longTail ? (
            <span className="font-medium text-secondary">
              {' · '}
              <span>
                Top 10 cover {bucketTotal > 0 ? formatPercent(topTenTotal / bucketTotal) : '—'}
              </span>
            </span>
          ) : null}
        </p>
      </header>
      <Renderer
        facet={facet}
        overlap={overlap}
        query={query}
        pinned={pinned}
        onPin={onPin}
        onDrillDown={onDrillDown}
      />
      {multiValued && !overlap ? (
        <p className="rounded-md bg-secondary px-2 py-1.5 text-xs text-tertiary">
          Garments can carry several {facet.label.toLocaleLowerCase()} values, so shares overlap.
        </p>
      ) : null}
      {selected ? (
        <p className="text-xs text-secondary" aria-live="polite">
          <span className="font-medium capitalize">{selected.value}</span>:{' '}
          {formatCount(selected.count)}
          {' · '}
          {counted > 0 ? formatPercent(selected.share) : '—'} counted
          {' · '}
          {selected.value === 'unknown' ? '—' : percent(selected.count, facet.known)} known
          {' · '}rank {bucketRank(allBuckets, selected)}
          {' · '}
          {comparison(selected.count, middle)}
        </p>
      ) : null}
    </section>
  );
}

type CrosstabNormalisation = 'count' | 'row' | 'column';

const NORMALISATIONS: { value: CrosstabNormalisation; label: string }[] = [
  { value: 'count', label: 'Count' },
  { value: 'row', label: '% of row' },
  { value: 'column', label: '% of column' },
];

function crosstabTitle(crosstab: AdminWardrobeCrosstab, weather = false): string {
  const xLabel = weather && crosstab.x_key === 'season' ? 'Season (weather)' : crosstab.x_label;
  const yLabel = weather && crosstab.y_key === 'season' ? 'Season (weather)' : crosstab.y_label;
  return `${xLabel} × ${yLabel}`;
}

const ORDINAL_CROSSTAB_KEYS = new Set(['warmth', 'versatility']);

function StackedAttributeComparison({
  crosstab,
  query,
  pinned,
  onDrillDown,
}: {
  crosstab: AdminWardrobeCrosstab;
  query: string;
  pinned: { facet: string; value: string } | undefined;
  onDrillDown: (title: string, matches: GarmentDrilldownMatch[]) => void;
}) {
  const ordinalIsX = ORDINAL_CROSSTAB_KEYS.has(crosstab.x_key);
  const ordinalKey = ordinalIsX ? crosstab.x_key : crosstab.y_key;
  const ordinalLabel = ordinalIsX ? crosstab.x_label : crosstab.y_label;
  const categoryKey = ordinalIsX ? crosstab.y_key : crosstab.x_key;
  const categoryValues = ordinalIsX ? (crosstab.y_values ?? []) : (crosstab.x_values ?? []);
  const ordinalValues = ordinalIsX ? (crosstab.x_values ?? []) : (crosstab.y_values ?? []);
  const categoryTotals = new Map(
    (ordinalIsX ? (crosstab.y_totals ?? []) : (crosstab.x_totals ?? [])).map((total) => [
      total.value,
      total.garments,
    ]),
  );
  const cells = new Map(
    (crosstab.cells ?? []).map((cell) => [`${cell.x}\0${cell.y}`, cell.garments] as const),
  );
  const cellCount = (category: string, band: string) =>
    cells.get(ordinalIsX ? `${band}\0${category}` : `${category}\0${band}`) ?? 0;
  const topBand = ordinalValues.at(-1);
  const strongestCategory = topBand
    ? categoryValues.reduce<string | undefined>((best, category) => {
        const share = cellCount(category, topBand) / Math.max(1, categoryTotals.get(category) ?? 0);
        const bestShare = best
          ? cellCount(best, topBand) / Math.max(1, categoryTotals.get(best) ?? 0)
          : -1;
        return share > bestShare ? category : best;
      }, undefined)
    : undefined;
  const headline =
    strongestCategory && topBand
      ? `${strongestCategory} garments skew ${topBand} (${percent(
          cellCount(strongestCategory, topBand),
          categoryTotals.get(strongestCategory) ?? 0,
        )})`
      : 'No populated combinations were returned.';
  const queryMatches = (value: string) => value.toLocaleLowerCase().includes(query);
  const rowDimmed = (category: string) =>
    Boolean(
      (query && !queryMatches(category)) ||
      (pinned?.facet === categoryKey && pinned.value !== category),
    );

  return (
    <article className="flex min-w-0 flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      <header>
        <h3 className="text-sm font-semibold text-primary">{crosstabTitle(crosstab, true)}</h3>
        <p className="text-xs font-medium text-secondary">{headline}</p>
      </header>
      <div
        role="group"
        aria-label={`${crosstabTitle(crosstab, true)} stacked bar chart`}
        className="flex flex-col gap-2"
      >
        {categoryValues.map((category) => {
          const total = categoryTotals.get(category) ?? 0;
          const pinnedRow = pinned?.facet === categoryKey && pinned.value === category;
          return (
            <div
              key={category}
              role="row"
              className={cx(
                'grid grid-cols-[6rem_minmax(0,1fr)_3.5rem] items-center gap-2 rounded-md transition-opacity',
                rowDimmed(category) && 'opacity-25',
                pinnedRow && 'ring-2 ring-brand',
              )}
            >
              <span role="rowheader" className="truncate text-xs text-secondary">
                {category}
              </span>
              <span className="flex h-7 overflow-hidden rounded-md bg-secondary">
                {ordinalValues.map((band, index) => {
                  const garments = cellCount(category, band);
                  const share = garments / Math.max(1, total);
                  const detail = `${category} · ${band} ${ordinalLabel.toLocaleLowerCase()}: ${formatCount(garments)} garments, ${percent(garments, total)} of ${category} garments`;
                  const ordinalDimmed = pinned?.facet === ordinalKey && pinned.value !== band;
                  const drillable =
                    category.toLocaleLowerCase() !== 'unknown' &&
                    band.toLocaleLowerCase() !== 'unknown';
                  return (
                    <Tooltip
                      key={band}
                      title={`${category} · ${band}`}
                      description={detail}
                      delay={0}
                    >
                      <TooltipTrigger
                        aria-label={detail}
                        className={cx(
                          'flex min-w-0 items-center justify-center overflow-hidden border-r border-primary text-[0.625rem] font-medium tabular-nums text-primary outline-brand last:border-r-0 focus-visible:z-10 focus-visible:outline-2',
                          ordinalDimmed && 'opacity-25',
                        )}
                        style={{
                          width: `${Math.max(0, share * 100)}%`,
                          background: heatmapBackground(
                            ordinalValues.length <= 1 ? 1 : index / (ordinalValues.length - 1),
                          ),
                        }}
                        onPress={
                          drillable
                            ? () =>
                                onDrillDown(`${category} × ${band}`, [
                                  {
                                    key: crosstab.x_key as GarmentDrilldownMatch['key'],
                                    value: ordinalIsX ? band : category,
                                  },
                                  {
                                    key: crosstab.y_key as GarmentDrilldownMatch['key'],
                                    value: ordinalIsX ? category : band,
                                  },
                                ])
                            : undefined
                        }
                      >
                        {share >= 0.08 ? formatPercent(share) : null}
                      </TooltipTrigger>
                    </Tooltip>
                  );
                })}
              </span>
              <span className="text-right text-xs tabular-nums text-tertiary">
                {formatCount(total)}
              </span>
            </div>
          );
        })}
      </div>
      <ul
        aria-label={`${ordinalLabel} band legend`}
        className="flex flex-wrap gap-x-3 gap-y-1 text-[0.625rem] text-tertiary"
      >
        {ordinalValues.map((band, index) => (
          <li key={band} className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-sm"
              aria-hidden="true"
              style={{
                background: heatmapBackground(
                  ordinalValues.length <= 1 ? 1 : index / (ordinalValues.length - 1),
                ),
              }}
            />
            <span>{band}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function HeatmapAttributeComparison({
  crosstab,
  query,
  pinned,
  onDrillDown,
}: {
  crosstab: AdminWardrobeCrosstab;
  query: string;
  pinned: { facet: string; value: string } | undefined;
  onDrillDown: (title: string, matches: GarmentDrilldownMatch[]) => void;
}) {
  const [normalisation, setNormalisation] = useState<CrosstabNormalisation>('count');
  const xValues = crosstab.x_values ?? [];
  const yValues = crosstab.y_values ?? [];
  const xTotals = new Map((crosstab.x_totals ?? []).map((total) => [total.value, total.garments]));
  const yTotals = new Map((crosstab.y_totals ?? []).map((total) => [total.value, total.garments]));
  const cells = new Map(
    (crosstab.cells ?? []).map((cell) => [`${cell.x}\0${cell.y}`, cell.garments] as const),
  );
  const values = (crosstab.cells ?? []).map((cell) => {
    if (normalisation === 'row') return cell.garments / Math.max(1, yTotals.get(cell.y) ?? 0);
    if (normalisation === 'column') return cell.garments / Math.max(1, xTotals.get(cell.x) ?? 0);
    return cell.garments;
  });
  const peak = Math.max(1, ...values);
  const xPeak = Math.max(1, ...xValues.map((value) => xTotals.get(value) ?? 0));
  const yPeak = Math.max(1, ...yValues.map((value) => yTotals.get(value) ?? 0));
  const pinRelevant = pinned?.facet === crosstab.x_key || pinned?.facet === crosstab.y_key;
  const queryMatches = (value: string) => value.toLocaleLowerCase().includes(query);
  const axisDimmed = (key: string, value: string) =>
    Boolean(
      (query && !queryMatches(value)) ||
      (pinRelevant && pinned?.facet === key && pinned.value !== value),
    );
  const cellActive = (x: string, y: string) =>
    (!query || queryMatches(x) || queryMatches(y)) &&
    (!pinRelevant ||
      (pinned?.facet === crosstab.x_key && pinned.value === x) ||
      (pinned?.facet === crosstab.y_key && pinned.value === y));
  const strongest = (crosstab.cells ?? []).reduce<
    NonNullable<AdminWardrobeCrosstab['cells']>[number] | undefined
  >((best, cell) => {
    const share = cell.garments / Math.max(1, xTotals.get(cell.x) ?? 0);
    const bestShare = best ? best.garments / Math.max(1, xTotals.get(best.x) ?? 0) : -1;
    return share > bestShare ? cell : best;
  }, undefined);
  const headline = strongest
    ? `${percent(strongest.garments, xTotals.get(strongest.x) ?? 0)} of ${strongest.x} garments are ${strongest.y}`
    : 'No populated combinations were returned.';
  const gridColumns = `6rem repeat(${xValues.length}, 2.25rem) 4.5rem`;

  return (
    <article className="flex min-w-0 flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      <header>
        <h3 className="text-sm font-semibold text-primary">{crosstabTitle(crosstab, true)}</h3>
        <p className="text-xs font-medium text-secondary">{headline}</p>
      </header>
      <div
        role="group"
        aria-label={`${crosstabTitle(crosstab, true)} normalisation`}
        className="flex flex-wrap gap-1"
      >
        {NORMALISATIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={normalisation === option.value}
            onClick={() => setNormalisation(option.value)}
            className={cx(
              'rounded-full border px-2 py-0.5 text-[0.625rem] font-medium outline-brand focus-visible:outline-2',
              normalisation === option.value
                ? 'border-brand bg-brand-primary text-brand-secondary'
                : 'border-secondary text-tertiary hover:bg-secondary',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto pb-1">
        <div
          role="grid"
          aria-label={`${crosstabTitle(crosstab, true)} heatmap`}
          className="grid min-w-max items-end gap-1"
          style={{ gridTemplateColumns: gridColumns }}
        >
          <span />
          {xValues.map((value) => (
            <span
              key={value}
              role="columnheader"
              className={cx(
                'truncate text-center text-[0.625rem] text-tertiary [writing-mode:vertical-rl]',
                axisDimmed(crosstab.x_key, value) && 'opacity-25',
              )}
            >
              {value}
            </span>
          ))}
          <span className="text-center text-[0.625rem] text-tertiary">Total</span>
          {yValues.map((y) => (
            <div key={y} role="row" className="contents">
              <span
                role="rowheader"
                className={cx(
                  'truncate text-xs text-secondary',
                  axisDimmed(crosstab.y_key, y) && 'opacity-25',
                )}
              >
                {y}
              </span>
              {xValues.map((x) => {
                const garments = cells.get(`${x}\0${y}`);
                const rowShare =
                  garments === undefined ? undefined : garments / Math.max(1, yTotals.get(y) ?? 0);
                const columnShare =
                  garments === undefined ? undefined : garments / Math.max(1, xTotals.get(x) ?? 0);
                const measure =
                  normalisation === 'row'
                    ? rowShare
                    : normalisation === 'column'
                      ? columnShare
                      : garments;
                const intensity = measure === undefined ? 0 : measure / peak;
                const visibleMeasure =
                  garments === undefined
                    ? '—'
                    : normalisation === 'count'
                      ? compactCount(garments)
                      : formatPercent(measure as number);
                const detail =
                  garments === undefined
                    ? `${x} × ${y}: —`
                    : normalisation === 'count'
                      ? `${x} × ${y}: ${formatCount(garments)} garments — ${percent(garments, xTotals.get(x) ?? 0)} of ${x} garments, ${percent(garments, yTotals.get(y) ?? 0)} of ${y} garments`
                      : `${x} × ${y}: ${formatPercent(measure as number)} of ${normalisation} — ${formatCount(garments)} garments, ${percent(garments, xTotals.get(x) ?? 0)} of ${x} garments, ${percent(garments, yTotals.get(y) ?? 0)} of ${y} garments`;
                const drillable =
                  x.toLocaleLowerCase() !== 'unknown' && y.toLocaleLowerCase() !== 'unknown';
                return (
                  <Tooltip key={x} title={`${x} × ${y}`} description={detail} delay={0}>
                    <TooltipTrigger
                      aria-label={detail}
                      className={cx(
                        'flex size-9 min-h-9 min-w-9 items-center justify-center rounded-sm border border-primary text-[0.6875rem] font-medium tabular-nums outline-brand transition-opacity focus-visible:outline-2',
                        garments !== undefined && intensity >= 0.55 ? 'text-white' : 'text-primary',
                        !cellActive(x, y) && 'opacity-20',
                      )}
                      data-intensity={garments === undefined ? undefined : intensity.toFixed(3)}
                      style={{
                        background:
                          garments === undefined
                            ? 'var(--color-bg-secondary)'
                            : heatmapBackground(intensity),
                      }}
                      onPress={
                        drillable
                          ? () =>
                              onDrillDown(`${x} × ${y}`, [
                                { key: crosstab.x_key as GarmentDrilldownMatch['key'], value: x },
                                { key: crosstab.y_key as GarmentDrilldownMatch['key'], value: y },
                              ])
                          : undefined
                      }
                    >
                      {visibleMeasure}
                    </TooltipTrigger>
                  </Tooltip>
                );
              })}
              <span
                role="img"
                aria-label={`${y} total: ${formatCount(yTotals.get(y) ?? 0)} garments`}
                className={cx(
                  'block h-2 overflow-hidden rounded-full bg-secondary',
                  axisDimmed(crosstab.y_key, y) && 'opacity-25',
                )}
              >
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${((yTotals.get(y) ?? 0) / yPeak) * 100}%`,
                    background: TONE_VARIABLE.neutral,
                  }}
                />
              </span>
            </div>
          ))}
          <span className="text-right text-[0.625rem] text-tertiary">Total</span>
          {xValues.map((value) => (
            <span
              key={value}
              role="img"
              aria-label={`${value} total: ${formatCount(xTotals.get(value) ?? 0)} garments`}
              className={cx(
                'flex h-8 items-end justify-center overflow-hidden rounded-sm bg-secondary',
                axisDimmed(crosstab.x_key, value) && 'opacity-25',
              )}
            >
              <span
                className="block w-2 rounded-t-sm"
                style={{
                  height: `${((xTotals.get(value) ?? 0) / xPeak) * 100}%`,
                  minHeight: (xTotals.get(value) ?? 0) > 0 ? '2px' : undefined,
                  background: TONE_VARIABLE.neutral,
                }}
              />
            </span>
          ))}
          <span />
        </div>
      </div>
    </article>
  );
}

function AttributeComparison({
  crosstab,
  query,
  pinned,
  onDrillDown,
}: {
  crosstab: AdminWardrobeCrosstab;
  query: string;
  pinned: { facet: string; value: string } | undefined;
  onDrillDown: (title: string, matches: GarmentDrilldownMatch[]) => void;
}) {
  const ordinalAxes =
    Number(ORDINAL_CROSSTAB_KEYS.has(crosstab.x_key)) +
    Number(ORDINAL_CROSSTAB_KEYS.has(crosstab.y_key));
  return ordinalAxes === 1 ? (
    <StackedAttributeComparison {...{ crosstab, query, pinned, onDrillDown }} />
  ) : (
    <HeatmapAttributeComparison {...{ crosstab, query, pinned, onDrillDown }} />
  );
}

function AttributeCoverage({ facets }: { facets: readonly AdminWardrobeFacet[] }) {
  const ordered = [...facets].sort((left, right) => {
    const leftTotal = left.known + left.unknown;
    const rightTotal = right.known + right.unknown;
    const leftCoverage = leftTotal > 0 ? left.known / leftTotal : -1;
    const rightCoverage = rightTotal > 0 ? right.known / rightTotal : -1;
    return rightCoverage - leftCoverage || facetPosition(left.key) - facetPosition(right.key);
  });

  return (
    <figure
      className="m-0 flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
      aria-labelledby="attribute-coverage-title"
    >
      <figcaption>
        <h3 id="attribute-coverage-title" className="text-sm font-semibold text-primary">
          Attribute coverage
        </h3>
        <p className="text-xs text-tertiary">
          Known versus unknown garments, highest coverage first.
        </p>
      </figcaption>
      <ul className="flex flex-col gap-2" aria-label="Attribute coverage, sorted highest to lowest">
        {ordered.map((facet) => {
          const total = facet.known + facet.unknown;
          const coverage = total > 0 ? facet.known / total : undefined;
          return (
            <li
              key={facet.key}
              className="grid grid-cols-[minmax(5rem,7rem)_minmax(0,1fr)_3rem] items-center gap-2"
            >
              <span className="truncate text-xs text-secondary">{facet.label}</span>
              <span className="flex h-3 overflow-hidden rounded-full bg-secondary">
                {facet.known > 0 && total > 0 ? (
                  <Tooltip
                    title={`${facet.label}: known`}
                    description={`${formatCount(facet.known)} known · ${formatPercent(facet.known / total)} coverage`}
                    delay={0}
                  >
                    <TooltipTrigger
                      aria-label={`${facet.label}: ${formatCount(facet.known)} known, ${formatPercent(facet.known / total)} coverage.`}
                      className="h-full outline-brand focus-visible:z-10 focus-visible:outline-2"
                      style={{
                        width: `${(facet.known / total) * 100}%`,
                        background: TONE_VARIABLE.info,
                      }}
                    >
                      <span className="sr-only">{formatCount(facet.known)} known</span>
                    </TooltipTrigger>
                  </Tooltip>
                ) : null}
                {facet.unknown > 0 && total > 0 ? (
                  <Tooltip
                    title={`${facet.label}: unknown`}
                    description={`${formatCount(facet.unknown)} unknown · ${formatPercent(facet.unknown / total)} of garments`}
                    delay={0}
                  >
                    <TooltipTrigger
                      aria-label={`${facet.label}: ${formatCount(facet.unknown)} unknown, ${formatPercent(facet.unknown / total)} of garments.`}
                      className="h-full outline-brand focus-visible:z-10 focus-visible:outline-2"
                      style={{
                        width: `${(facet.unknown / total) * 100}%`,
                        background: TONE_VARIABLE.neutral,
                      }}
                    >
                      <span className="sr-only">{formatCount(facet.unknown)} unknown</span>
                    </TooltipTrigger>
                  </Tooltip>
                ) : null}
              </span>
              <span className="text-right text-xs tabular-nums text-tertiary">
                {coverage === undefined ? '—' : formatPercent(coverage)}
              </span>
            </li>
          );
        })}
      </ul>
      <ul className="flex gap-4 text-xs text-tertiary" aria-hidden="true">
        <li className="flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ background: TONE_VARIABLE.info }} /> Known
        </li>
        <li className="flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ background: TONE_VARIABLE.neutral }} />{' '}
          Unknown
        </li>
      </ul>
    </figure>
  );
}

function closetRank(buckets: readonly AdminClosetSizeBucket[], bucket: AdminClosetSizeBucket) {
  return (
    [...buckets]
      .sort((left, right) => right.members - left.members || left.min - right.min)
      .findIndex((candidate) => candidate.label === bucket.label) + 1
  );
}

function inClosetBucket(size: number, bucket: AdminClosetSizeBucket): boolean {
  return size >= bucket.min && (bucket.max === null || size <= bucket.max);
}

function ClosetSizes({
  data,
}: {
  data: NonNullable<AdminWardrobeCompositionSummary['closet_sizes']>;
}) {
  const [selected, setSelected] = useState<string>();
  const buckets = [...(data.buckets ?? [])].sort((left, right) => left.min - right.min);
  const total = count(data.members);
  const bucket = buckets.find((candidate) => candidate.label === selected);
  const largest = bucket ? (data.largest ?? []).filter((size) => inClosetBucket(size, bucket)) : [];
  const medianInside = bucket && data.median != null && inClosetBucket(data.median, bucket);
  const peak = Math.max(1, ...buckets.map((item) => item.members));
  const medianBucket =
    data.median == null ? -1 : buckets.findIndex((item) => inClosetBucket(data.median ?? 0, item));
  const medianPosition =
    medianBucket < 0 || !buckets.length ? undefined : ((medianBucket + 0.5) / buckets.length) * 100;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      {buckets.length ? (
        <div role="group" aria-label="Closet size histogram" className="flex flex-col gap-2">
          <div className="relative h-40 border-b border-secondary">
            {medianPosition !== undefined ? (
              <div
                role="img"
                tabIndex={0}
                aria-label={`Median rule: ${data.median?.toLocaleString(undefined, { maximumFractionDigits: 1 })} garments`}
                className="pointer-events-none absolute inset-y-0 z-20 border-l-2 border-dashed outline-brand focus-visible:outline-2"
                style={{ left: `${medianPosition}%`, borderColor: TONE_VARIABLE.warning }}
              >
                <span className="absolute top-0 left-1 whitespace-nowrap text-[0.625rem] font-medium text-tertiary">
                  Median {data.median?.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                </span>
              </div>
            ) : null}
            <div
              className="absolute inset-0 grid items-stretch gap-1"
              style={{
                gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
                gridTemplateRows: '100%',
              }}
            >
              {buckets.map((item) => {
                const rank = closetRank(buckets, item);
                const detail = `${formatCount(item.members)} members · ${percent(
                  item.members,
                  total,
                )} of counted members · rank ${rank} of ${formatCount(buckets.length)}`;
                const active = selected === item.label;
                return (
                  <Tooltip
                    key={item.label}
                    title={`${item.label} garments`}
                    description={detail}
                    delay={0}
                  >
                    <TooltipTrigger
                      aria-label={`${item.label} garments: ${detail}. ${active ? 'Hide' : 'Show'} bucket detail.`}
                      aria-pressed={active}
                      onPress={() => setSelected(active ? undefined : item.label)}
                      className={cx(
                        'flex h-full items-end rounded-t-sm outline-brand focus-visible:outline-2',
                        active && 'ring-2 ring-brand',
                      )}
                    >
                      <span
                        className="block w-full rounded-t-sm"
                        style={{
                          height: `${(item.members / peak) * 100}%`,
                          minHeight: item.members > 0 ? '2px' : undefined,
                          background: TONE_VARIABLE.info,
                        }}
                      />
                    </TooltipTrigger>
                  </Tooltip>
                );
              })}
            </div>
          </div>
          <div
            className="grid gap-1 text-center text-[0.625rem] text-tertiary"
            style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}
          >
            {buckets.map((item) => (
              <span key={item.label} className="truncate">
                {item.label}
              </span>
            ))}
          </div>
          <p className="sr-only">
            {buckets
              .map((item) => `${item.label} garments: ${formatCount(item.members)} members`)
              .join(', ')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-tertiary">No closet sizes have been counted yet.</p>
      )}

      {bucket ? (
        <div
          className="rounded-lg bg-secondary px-3 py-2 text-xs text-secondary"
          aria-live="polite"
        >
          <p>
            <span className="font-medium">{bucket.label} garments:</span>{' '}
            {largest.length
              ? `${largest.map((size) => formatCount(size)).join(', ')} among the 10 largest closets.`
              : 'No entries from the 10 largest closets fall in this bucket.'}
          </p>
          <p className="text-tertiary">
            {data.median == null
              ? 'Median closet size is not available.'
              : `Median: ${data.median.toLocaleString(undefined, { maximumFractionDigits: 1 })} garments (${medianInside ? 'inside' : 'outside'} this range).`}
          </p>
          {/* The aggregate has no member or garment rows; a deeper drill-down needs a backend filter. */}
        </div>
      ) : null}

      <div className="flex items-baseline justify-between gap-3 border-t border-secondary pt-3">
        <span className="text-xs font-medium text-tertiary uppercase">Median closet</span>
        {data.median == null ? (
          <span className="text-sm text-tertiary">Not enough data</span>
        ) : (
          <Quantity
            value={data.median.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            unit="garments"
            raw={data.median}
          />
        )}
      </div>
      <p className="text-xs text-tertiary">
        Large closets can dominate totals; compare the distribution with the median member.
      </p>
    </div>
  );
}

type DrilldownSelection = {
  title: string;
  matches: GarmentDrilldownMatch[];
};

function GarmentDrilldownDialog({
  selection,
  onClose,
}: {
  selection: DrilldownSelection;
  onClose: () => void;
}) {
  const [items, setItems] = useState<AdminWardrobeCompositionGarment[]>([]);
  const [total, setTotal] = useState<number>();
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const matchSignature = selection.matches.map(({ key, value }) => `${key}:${value}`).join('\0');
  const loadPage = useCallback(
    async (offset: number, append: boolean) => {
      setLoading(true);
      setError(undefined);
      try {
        const result = await fetchGarmentDrilldown(selection.matches, offset);
        setItems((current) => (append ? [...current, ...result.items] : result.items));
        setTotal(result.total);
        setNextOffset(result.offset + result.limit);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Garments could not be loaded.');
      } finally {
        setLoading(false);
      }
    },
    // The signature changes exactly when the request filters do; the selection is immutable while open.
    [matchSignature],
  );

  useEffect(() => void loadPage(0, false), [loadPage]);

  return (
    <ActionDialog
      title={selection.title}
      description={
        total === undefined
          ? 'Matching garments'
          : `${formatCount(total)} ${plural(total, 'garment')}`
      }
      width="lg"
      onClose={onClose}
    >
      {error && !items.length ? (
        <Callout
          tone="error"
          title="Matching garments could not be loaded."
          actions={
            <Button size="sm" color="secondary" onClick={() => void loadPage(0, false)}>
              Try again
            </Button>
          }
        >
          <FailureText raw={error} />
        </Callout>
      ) : loading && !items.length ? (
        <div className="flex items-center gap-2 py-8 text-sm text-tertiary" aria-live="polite">
          <LoadingIndicator size="sm" type="dot-circle" />
          Loading garments…
        </div>
      ) : total === 0 ? (
        <div className="rounded-xl border border-secondary bg-secondary p-6 text-sm text-tertiary">
          No garments match this data point.
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => {
              const title = item.title?.trim() || 'Untitled garment';
              const href = `/consumers/${encodeURIComponent(item.member_id)}?tab=wardrobe&garment=${encodeURIComponent(item.garment_id)}`;
              return (
                <li key={item.garment_id} className="min-w-0">
                  <Link
                    to={href}
                    className="flex h-full min-w-0 flex-col gap-2 rounded-xl border border-secondary bg-primary p-3 outline-brand hover:border-brand focus-visible:outline-2"
                  >
                    <GarmentImage
                      url={item.image_url}
                      refPresent={item.image_url}
                      alt={`${title} garment`}
                      className="aspect-square w-full rounded-lg"
                      absentDetail="No image for this garment."
                    />
                    <span className="truncate text-sm font-medium text-primary" title={title}>
                      {title}
                    </span>
                    <span className="truncate text-xs text-tertiary">
                      {[item.category, item.subcategory].filter(Boolean).join(' · ') ||
                        'No category'}
                    </span>
                    <span className="truncate text-xs text-secondary">{item.member_label}</span>
                    {item.colours.length || item.occasions.length ? (
                      <span className="flex flex-wrap gap-1">
                        {[...item.colours, ...item.occasions].map((tag, index) => (
                          <span
                            key={`${tag}-${index}`}
                            className="rounded-full bg-secondary px-2 py-0.5 text-[0.625rem] text-tertiary"
                          >
                            {tag}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
          {error ? (
            <Callout
              tone="error"
              title="More garments could not be loaded."
              actions={
                <Button size="sm" color="secondary" onClick={() => void loadPage(nextOffset, true)}>
                  Try again
                </Button>
              }
            />
          ) : total !== undefined && items.length < total ? (
            <div className="flex justify-center border-t border-secondary pt-4">
              <Button
                size="sm"
                color="secondary"
                isDisabled={loading}
                onClick={() => void loadPage(nextOffset, true)}
              >
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </ActionDialog>
  );
}

function GarmentDataSummary({ data }: { data: AdminWardrobeCompositionSummary }) {
  const [params, setParams] = useSearchParams();
  const [pinned, setPinned] = useState<{ facet: string; value: string }>();
  const [drilldown, setDrilldown] = useState<DrilldownSelection>();
  const active = count(data.active_garments);
  const attributed = count(data.attributed_garments);
  const facets = [...(data.facets ?? [])].sort(
    (left, right) => facetPosition(left.key) - facetPosition(right.key),
  );
  const overlaps = new Map((data.facet_overlaps ?? []).map((overlap) => [overlap.key, overlap]));
  const crosstabs = data.crosstabs;
  const added = data.added_by_month;
  const timeline = added ? monthTimeline(added) : [];
  const empty = count(data.total_garments) === 0 && facets.length === 0;
  const configuredFacets = params.get(FACETS_PARAM);
  const facetKeys = new Set(facets.map((facet) => facet.key));
  const visibleKeys = new Set(
    configuredFacets === null
      ? facets.map((facet) => facet.key)
      : configuredFacets.split(',').filter((key) => facetKeys.has(key)),
  );
  const search = params.get(SEARCH_PARAM) ?? '';
  const query = search.trim().toLocaleLowerCase();
  const configuredPairs = params.get(PAIRS_PARAM);
  const pairKeys = new Set((crosstabs ?? []).map((crosstab) => crosstab.key));
  const defaultPairKeys = (crosstabs ?? [])
    .slice(0, DEFAULT_CROSSTAB_LIMIT)
    .map((crosstab) => crosstab.key);
  const visiblePairKeys = new Set(
    configuredPairs === null
      ? defaultPairKeys
      : configuredPairs.split(',').filter((key) => pairKeys.has(key)),
  );
  const visibleCrosstabs = (crosstabs ?? []).filter((crosstab) =>
    visiblePairKeys.has(crosstab.key),
  );
  const visibleFacets = facets.filter(
    (facet) =>
      visibleKeys.has(facet.key) &&
      (!query ||
        (facet.buckets ?? []).some((bucket) => bucket.value.toLocaleLowerCase().includes(query))),
  );
  const visibleStandardFacets = visibleFacets.filter((facet) => !overlaps.has(facet.key));
  const visibleOverlapFacets = visibleFacets.filter((facet) => overlaps.has(facet.key));
  const writeParams = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };
  const toggleFacet = (key: string) => {
    const nextKeys = new Set(visibleKeys);
    if (nextKeys.has(key)) nextKeys.delete(key);
    else nextKeys.add(key);
    writeParams((next) => {
      if (nextKeys.size === facets.length) next.delete(FACETS_PARAM);
      else {
        next.set(
          FACETS_PARAM,
          facets
            .map((facet) => facet.key)
            .filter((item) => nextKeys.has(item))
            .join(','),
        );
      }
    });
  };
  const writePairKeys = (keys: Set<string>) => {
    writeParams((next) => {
      if (keys.size === defaultPairKeys.length && defaultPairKeys.every((key) => keys.has(key))) {
        next.delete(PAIRS_PARAM);
      } else {
        next.set(
          PAIRS_PARAM,
          (crosstabs ?? [])
            .map((crosstab) => crosstab.key)
            .filter((key) => keys.has(key))
            .join(','),
        );
      }
    });
  };
  const togglePair = (key: string) => {
    const nextKeys = new Set(visiblePairKeys);
    if (nextKeys.has(key)) nextKeys.delete(key);
    else nextKeys.add(key);
    writePairKeys(nextKeys);
  };

  return (
    <div
      className="flex flex-col gap-6"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && pinned) {
          event.preventDefault();
          setPinned(undefined);
        }
      }}
    >
      <StatRow>
        <StatTile
          label="Members"
          value={
            <Quantity
              value={formatCount(count(data.member_count))}
              unit={plural(count(data.member_count), 'member')}
              raw={count(data.member_count)}
            />
          }
        />
        <StatTile
          label="Active garments"
          value={
            <Quantity value={formatCount(active)} unit={plural(active, 'garment')} raw={active} />
          }
        />
        <StatTile
          label="Archived garments"
          value={
            <Quantity
              value={formatCount(count(data.archived_garments))}
              unit={plural(count(data.archived_garments), 'garment')}
              raw={count(data.archived_garments)}
            />
          }
        />
        <StatTile
          label="Attributed garments"
          value={
            <Quantity
              value={formatCount(attributed)}
              unit={plural(attributed, 'garment')}
              raw={attributed}
            />
          }
          hint={
            active > 0
              ? `${formatPercent(attributed / active)} of active garments`
              : 'There are no active garments to compare.'
          }
          {...(active > 0
            ? {
                bar: {
                  fraction: attributed / active,
                  label: `Attributed garments as a share of active garments: ${attributed} of ${active}`,
                },
              }
            : {})}
        />
        <StatTile
          label="Never worn"
          value={
            <Quantity
              value={formatCount(count(data.never_worn))}
              unit={plural(count(data.never_worn), 'garment')}
              raw={count(data.never_worn)}
            />
          }
        />
        <StatTile
          label="Total wears"
          value={
            <Quantity
              value={formatCount(count(data.total_wears))}
              unit={plural(count(data.total_wears), 'wear')}
              raw={count(data.total_wears)}
            />
          }
        />
      </StatRow>

      {data.generated_at ? (
        <p className="text-sm text-tertiary">
          Computed {new Date(data.generated_at).toLocaleString()}. This result is cached until a
          garment changes.
        </p>
      ) : null}

      {empty ? (
        <Callout tone="neutral" title="No garment data has been recorded yet.">
          The read succeeded. Garment composition will appear after members add garments.
        </Callout>
      ) : null}

      {facets.length ? (
        <Section
          title="Garment attributes"
          description="AI-estimated coverage across active garments."
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AttributeCoverage facets={facets} />
          </div>
        </Section>
      ) : null}

      {data.closet_sizes || added ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {data.closet_sizes ? (
            <Section
              title="Closet sizes"
              description={`Distribution across ${formatCount(count(data.closet_sizes.members))} ${plural(count(data.closet_sizes.members), 'member')}. Select a bucket for context.`}
            >
              <ClosetSizes data={data.closet_sizes} />
            </Section>
          ) : null}

          {added ? (
            <Section
              title="Garments added per month"
              description="Garments digitized by calendar month; point or focus for exact counts."
            >
              {timeline.length ? (
                <Timeline
                  buckets={timeline}
                  series={[{ id: 'garments', label: 'Garments added', tone: 'info' }]}
                  noun="garments added"
                />
              ) : (
                <p className="rounded-xl border border-secondary bg-primary p-4 text-sm text-tertiary">
                  No monthly garment history has been recorded yet.
                </p>
              )}
            </Section>
          ) : null}
        </div>
      ) : null}

      {facets.length ? (
        <Section
          title="Attribute breakdowns"
          description="AI-estimated values across active garments."
        >
          <div className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-3">
            <div
              role="group"
              className="flex flex-wrap gap-2"
              aria-label="Visible attribute facets"
            >
              {facets.map((facet) => {
                const visible = visibleKeys.has(facet.key);
                return (
                  <button
                    key={facet.key}
                    type="button"
                    aria-pressed={visible}
                    onClick={() => toggleFacet(facet.key)}
                    className={cx(
                      'rounded-full border px-3 py-1 text-xs font-medium outline-brand focus-visible:outline-2',
                      visible
                        ? 'border-brand bg-brand-primary text-brand-secondary'
                        : 'border-secondary bg-primary text-tertiary hover:bg-primary_hover',
                    )}
                  >
                    {facet.label}
                  </button>
                );
              })}
            </div>
            <div className="max-w-sm">
              <TextField
                label="Filter bucket values"
                type="search"
                value={search}
                placeholder="e.g. silk"
                onChange={(event) =>
                  writeParams((next) => {
                    if (event.target.value) next.set(SEARCH_PARAM, event.target.value);
                    else next.delete(SEARCH_PARAM);
                  })
                }
              />
            </div>
          </div>
          {visibleFacets.length ? (
            <>
              {visibleStandardFacets.length ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {visibleStandardFacets.map((facet) => (
                    <Facet
                      key={facet.key}
                      facet={facet}
                      query={query}
                      pinned={pinned?.facet === facet.key ? pinned.value : undefined}
                      onPin={(value) => setPinned(value ? { facet: facet.key, value } : undefined)}
                      onDrillDown={(title, matches) => setDrilldown({ title, matches })}
                    />
                  ))}
                </div>
              ) : null}
              {visibleOverlapFacets.length ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {visibleOverlapFacets.map((facet) => (
                    <Facet
                      key={facet.key}
                      facet={facet}
                      overlap={overlaps.get(facet.key)}
                      query={query}
                      pinned={pinned?.facet === facet.key ? pinned.value : undefined}
                      onPin={(value) => setPinned(value ? { facet: facet.key, value } : undefined)}
                      onDrillDown={(title, matches) => setDrilldown({ title, matches })}
                    />
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className="rounded-xl border border-secondary bg-primary p-4 text-sm text-tertiary">
              {query
                ? 'No visible attribute values match this filter.'
                : 'No attribute facets are visible.'}
            </p>
          )}
        </Section>
      ) : null}

      {crosstabs?.length ? (
        <Section
          title="Attribute comparisons"
          description="Cross-tabulations among garments with both attributes known."
        >
          <div className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-3">
            <div
              role="group"
              className="flex flex-wrap gap-2"
              aria-label="Visible attribute comparisons"
            >
              {crosstabs.map((crosstab) => {
                const visible = visiblePairKeys.has(crosstab.key);
                return (
                  <button
                    key={crosstab.key}
                    type="button"
                    aria-pressed={visible}
                    onClick={() => togglePair(crosstab.key)}
                    className={cx(
                      'rounded-full border px-3 py-1 text-xs font-medium outline-brand focus-visible:outline-2',
                      visible
                        ? 'border-brand bg-brand-primary text-brand-secondary'
                        : 'border-secondary bg-primary text-tertiary hover:bg-primary_hover',
                    )}
                  >
                    {crosstabTitle(crosstab)}
                  </button>
                );
              })}
            </div>
            {crosstabs.length > DEFAULT_CROSSTAB_LIMIT ? (
              <button
                type="button"
                aria-pressed={visiblePairKeys.size === crosstabs.length}
                onClick={() =>
                  writePairKeys(
                    visiblePairKeys.size === crosstabs.length
                      ? new Set(defaultPairKeys)
                      : new Set(crosstabs.map((crosstab) => crosstab.key)),
                  )
                }
                className="self-start rounded-md text-xs font-medium text-brand-secondary underline outline-brand focus-visible:outline-2"
              >
                {visiblePairKeys.size === crosstabs.length
                  ? `Show default ${Math.min(DEFAULT_CROSSTAB_LIMIT, crosstabs.length)}`
                  : `Show all ${crosstabs.length}`}
              </button>
            ) : null}
          </div>
          {visibleCrosstabs.length ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {visibleCrosstabs.map((crosstab) => (
                <AttributeComparison
                  key={crosstab.key}
                  crosstab={crosstab}
                  query={query}
                  pinned={pinned}
                  onDrillDown={(title, matches) => setDrilldown({ title, matches })}
                />
              ))}
            </div>
          ) : (
            <p className="rounded-xl border border-secondary bg-primary p-4 text-sm text-tertiary">
              No attribute comparisons are visible.
            </p>
          )}
        </Section>
      ) : null}
      {drilldown ? (
        <GarmentDrilldownDialog
          key={drilldown.matches.map(({ key, value }) => `${key}:${value}`).join(',')}
          selection={drilldown}
          onClose={() => setDrilldown(undefined)}
        />
      ) : null}
    </div>
  );
}

export function GarmentData() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      configureAdminApi();
      const result = await wardrobeCompositionApiV1InternalWardrobeCompositionGet();
      handleUnauthorized(result.response?.status);
      if (result.error || !result.data) {
        setState({
          status: 'failed',
          detail: errorDetail(result.error, 'request_failed'),
        });
        return;
      }
      setState({ status: 'ready', data: result.data });
    } catch (cause) {
      setState({
        status: 'failed',
        detail: errorDetail(cause, 'request_failed'),
      });
    }
  }, []);
  useEffect(() => void load(), [load]);

  return (
    <Page
      title="Garment data"
      purpose="Use this page to understand the size and composition of wardrobes across the pilot. This is a read-only fleet view."
    >
      {state.status === 'loading' ? (
        <div className="flex items-center gap-2 py-8 text-sm text-tertiary" aria-live="polite">
          <LoadingIndicator size="sm" type="dot-circle" />
          Loading…
        </div>
      ) : state.status === 'failed' ? (
        <Callout
          tone="error"
          title="Garment data could not be loaded."
          actions={
            <Button size="sm" color="secondary" onClick={() => void load()}>
              Try again
            </Button>
          }
        >
          <FailureText raw={state.detail} />
        </Callout>
      ) : (
        <GarmentDataSummary data={state.data} />
      )}
    </Page>
  );
}
